import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-charcoal py-10 px-6 border-t border-gray-800 text-center text-gray-500 text-sm" data-id="element-89">
        <div className="max-w-4xl mx-auto space-y-5" data-id="element-90">
          <p data-id="element-91">
            © 2026 Plan Cero Gritos Post-Pantalla | Todos los derechos
            reservados
          </p>
          <div className="flex justify-center gap-6" data-id="element-92">
            <button onClick={() => setActiveModal('terms')} className="text-gray-400 hover:text-white underline underline-offset-4 transition-colors" data-id="element-93">
              Términos y Condiciones
            </button>
            <span className="text-gray-700" data-id="element-94">|</span>
            <button onClick={() => setActiveModal('privacy')} className="text-gray-400 hover:text-white underline underline-offset-4 transition-colors" data-id="element-95">
              Política de Privacidad
            </button>
          </div>
          <p className="text-xs opacity-70" data-id="element-96">
            Este producto es digital. No se envían físicos. Los resultados
            pueden variar según la consistencia de aplicación.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-97">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setActiveModal(null)} data-id="element-98">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 30
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 30
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-99">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 md:p-8 border-b border-cream-dark shrink-0" data-id="element-100">
                <h2 className="text-2xl md:text-3xl font-bold font-serif text-charcoal" data-id="element-101">
                  {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 bg-cream hover:bg-cream-dark rounded-full flex items-center justify-center transition-colors shrink-0" aria-label="Cerrar" data-id="element-102">
                  <X className="w-5 h-5 text-charcoal" data-id="element-103" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto p-6 md:p-8 text-charcoal-light leading-relaxed text-sm md:text-base" data-id="element-104">
                {activeModal === 'terms' ? <TermsContent data-id="element-105" /> : <PrivacyContent data-id="element-106" />}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-cream-dark shrink-0 text-center" data-id="element-107">
                <button onClick={() => setActiveModal(null)} className="bg-charcoal hover:bg-charcoal-light text-white font-bold py-3 px-8 rounded-xl transition-colors" data-id="element-108">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function TermsContent() {
  return <div className="space-y-6" data-id="element-109">
      <p className="text-xs text-gray-400" data-id="element-110">
        Última actualización: 21 de abril de 2026
      </p>

      <div className="space-y-4" data-id="element-111">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-112">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-113">
          Al acceder y realizar una compra en nuestra plataforma, usted acepta
          cumplir con estos Términos y Condiciones. Si no está de acuerdo con
          alguna parte de estos términos, le solicitamos que no realice la
          compra.
        </p>
      </div>

      <div className="space-y-4" data-id="element-114">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-115">
          2. Descripción del Producto
        </h3>
        <p data-id="element-116">
          El "Plan Cero Gritos Post-Pantalla" es un producto digital en formato
          PDF descargable. No se realizan envíos físicos. El contenido incluye
          estrategias, técnicas y herramientas basadas en principios de
          neurociencia aplicada a la crianza para gestionar las transiciones de
          pantalla en niños.
        </p>
      </div>

      <div className="space-y-4" data-id="element-117">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-118">
          3. Entrega del Producto
        </h3>
        <p data-id="element-119">
          Una vez confirmado el pago, recibirá acceso inmediato al producto
          digital mediante un enlace de descarga. Es su responsabilidad guardar
          el archivo en un lugar seguro. No nos hacemos responsables por la
          pérdida del archivo después de la descarga.
        </p>
      </div>

      <div className="space-y-4" data-id="element-120">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-121">
          4. Política de Reembolso
        </h3>
        <p data-id="element-122">
          Ofrecemos una garantía de devolución de dinero de 30 días. Si aplica
          el método durante al menos 7 días y no observa ningún cambio, puede
          solicitar un reembolso completo del 100% sin preguntas. Para solicitar
          un reembolso, simplemente comuníquese con nosotros dentro del período
          de 30 días posteriores a la compra.
        </p>
      </div>

      <div className="space-y-4" data-id="element-123">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-124">
          5. Propiedad Intelectual
        </h3>
        <p data-id="element-125">
          Todo el contenido del Plan Cero Gritos Post-Pantalla, incluyendo
          textos, gráficos, plantillas y metodologías, está protegido por
          derechos de autor. Queda prohibida la reproducción, distribución o
          reventa total o parcial del material sin autorización expresa por
          escrito.
        </p>
      </div>

      <div className="space-y-4" data-id="element-126">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-127">6. Uso Personal</h3>
        <p data-id="element-128">
          El producto está destinado exclusivamente para uso personal y familiar
          del comprador. No está permitido compartir, distribuir o utilizar el
          contenido con fines comerciales, incluyendo la reventa, la
          reproducción masiva o la incorporación en otros productos o servicios.
        </p>
      </div>

      <div className="space-y-4" data-id="element-129">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-130">
          7. Descargo de Responsabilidad
        </h3>
        <p data-id="element-131">
          El contenido de este producto es de carácter informativo y educativo.
          No sustituye el consejo médico, psicológico o profesional. Los
          resultados pueden variar según la consistencia de aplicación, la edad
          del niño y las circunstancias individuales de cada familia. No
          garantizamos resultados específicos.
        </p>
      </div>

      <div className="space-y-4" data-id="element-132">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-133">8. Modificaciones</h3>
        <p data-id="element-134">
          Nos reservamos el derecho de modificar estos Términos y Condiciones en
          cualquier momento. Las modificaciones entrarán en vigor a partir de su
          publicación. El uso continuado del producto después de cualquier
          cambio constituye la aceptación de los nuevos términos.
        </p>
      </div>
    </div>;
}
function PrivacyContent() {
  return <div className="space-y-6" data-id="element-135">
      <p className="text-xs text-gray-400" data-id="element-136">
        Última actualización: 21 de abril de 2026
      </p>

      <div className="space-y-4" data-id="element-137">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-138">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-139">
          Al realizar una compra, recopilamos únicamente la información
          necesaria para procesar su pedido, que puede incluir:
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-140">
          <li data-id="element-141">Nombre completo</li>
          <li data-id="element-142">Dirección de correo electrónico</li>
          <li data-id="element-143">
            Información de pago (procesada de forma segura por nuestro proveedor
            de pagos)
          </li>
        </ul>
      </div>

      <div className="space-y-4" data-id="element-144">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-145">
          2. Uso de la Información
        </h3>
        <p data-id="element-146">La información recopilada se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-147">
          <li data-id="element-148">Procesar y entregar su compra</li>
          <li data-id="element-149">Enviar el enlace de descarga del producto</li>
          <li data-id="element-150">Gestionar solicitudes de reembolso</li>
          <li data-id="element-151">Enviar comunicaciones relacionadas con su compra</li>
        </ul>
      </div>

      <div className="space-y-4" data-id="element-152">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-153">
          3. Protección de Datos
        </h3>
        <p data-id="element-154">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger su información personal contra el acceso no autorizado, la
          alteración, la divulgación o la destrucción. Los pagos son procesados
          a través de plataformas seguras con encriptación SSL.
        </p>
      </div>

      <div className="space-y-4" data-id="element-155">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-156">
          4. No Compartimos su Información
        </h3>
        <p data-id="element-157">
          No vendemos, intercambiamos ni transferimos su información personal a
          terceros. La única excepción es nuestro procesador de pagos, que
          necesita cierta información para completar la transacción de forma
          segura.
        </p>
      </div>

      <div className="space-y-4" data-id="element-158">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-159">
          5. Cookies y Tecnologías de Seguimiento
        </h3>
        <p data-id="element-160">
          Nuestro sitio web puede utilizar cookies y tecnologías similares para
          mejorar la experiencia del usuario y analizar el tráfico del sitio.
          Usted puede configurar su navegador para rechazar cookies, aunque esto
          podría afectar la funcionalidad del sitio.
        </p>
      </div>

      <div className="space-y-4" data-id="element-161">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-162">6. Sus Derechos</h3>
        <p data-id="element-163">Usted tiene derecho a:</p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-164">
          <li data-id="element-165">Acceder a la información personal que tenemos sobre usted</li>
          <li data-id="element-166">Solicitar la corrección de datos inexactos</li>
          <li data-id="element-167">Solicitar la eliminación de sus datos personales</li>
          <li data-id="element-168">
            Retirar su consentimiento para el uso de sus datos en cualquier
            momento
          </li>
        </ul>
      </div>

      <div className="space-y-4" data-id="element-169">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-170">7. Menores de Edad</h3>
        <p data-id="element-171">
          Este producto está dirigido a padres y cuidadores adultos. No
          recopilamos intencionalmente información personal de menores de 18
          años. Si descubrimos que hemos recopilado información de un menor, la
          eliminaremos de inmediato.
        </p>
      </div>

      <div className="space-y-4" data-id="element-172">
        <h3 className="text-lg font-bold text-charcoal" data-id="element-173">
          8. Cambios en esta Política
        </h3>
        <p data-id="element-174">
          Nos reservamos el derecho de actualizar esta Política de Privacidad en
          cualquier momento. Cualquier cambio será publicado en esta página con
          la fecha de actualización correspondiente. Le recomendamos revisar
          esta política periódicamente.
        </p>
      </div>
    </div>;
}